import { API_BASE_URL } from '@/config/api-url';

/**
 * checkoutReturnProof.ts — WEB-2353 (усиление WEB-2349, решение владельца
 * 22.09.2026).
 *
 * ЧТО ЭТО. Возврат с платёжной системы приходит на адрес вида
 * `…?checkout=success|cancel&s=paywall&rp=<признак>`. Признак кладёт НАШ
 * сервер в момент создания платёжной сессии (backend/src/paywalls/services/
 * checkout-return-proof.service.ts) и подписывает своим ключом.
 *
 * ЗАЧЕМ. До этой карточки экран оплаты при возврате монтировался ТОЛЬКО по
 * следу «этот браузер сам дошёл до экрана оплаты» (`paywallStepProof.ts`,
 * защита WEB-1740 от «переслал ссылку — попал сразу на оплату»). След живёт в
 * браузере, поэтому смена браузера или устройства между уходом на оплату и
 * возвратом, чистка хранилища, режим «инкогнито» и отказ посетителя от
 * хранения (WEB-2349) роняли УЖЕ ЗАПЛАТИВШЕГО человека на первый экран квиза
 * — без подтверждения покупки и без ссылки в приложение.
 *
 * ПОЧЕМУ ЧЕРЕЗ СЕРВЕР, А НЕ ПРОВЕРКОЙ НА МЕСТЕ. Подпись симметричная, ключ
 * серверный; отдать его в браузер значило бы отдать и возможность подделать
 * признак — то есть вернуть ровно ту дыру, которую закрывал WEB-1740. Поэтому
 * плеер только предъявляет признак и получает «да/нет».
 *
 * ПРИЗНАК ГОДЕН ОДНОЙ СТРАНИЦЕ, ОДНОМУ ПРЕДЪЯВИТЕЛЮ И НЕДОЛГО (фикс волны 3
 * находка 2, волны 5 находка 3, волны 6 находка 1). Вопрос серверу несёт три
 * вещи: сам признак, СТРАНИЦУ предъявления и случайный НОМЕР ПРЕДЪЯВИТЕЛЯ
 * этой загрузки. Первое успешное «да» ставит на сервере отметку; дальше
 * «да» получает только тот же предъявитель той же страницы и только внутри
 * короткого окна (чтобы потерянный ответ — обрыв сети, 502 края,
 * перезагрузка вкладки — можно было переспросить). Пересланный адрес
 * постороннему шага оплаты не открывает: страница у него та же по
 * построению, а номер предъявления свой.
 *
 * ПОЧЕМУ ОТДЕЛЬНАЯ РУЧКА, А НЕ ПОЛЕ В ОТВЕТЕ ПОКАЗА. Ответ показа воронки
 * кешируется (Redis у сервера, «последний хороший» у воркера на краю) и
 * общий для всех посетителей; признак — личный и короткоживущий, в общем кеше
 * ему не место. Отдельный вопрос дешёв (одно булево) и идёт ПАРАЛЛЕЛЬНО с
 * загрузкой воронки, лишнего ожидания покупателю не добавляя.
 *
 * ЛЮБОЙ СБОЙ = «ПРИЗНАКА НЕТ». Сеть, 5xx, чужой ответ — всё читается как
 * «нет», и возврат идёт прежним путём: по следу в браузере. Это усиление, а
 * не замена: новый путь не может сделать хуже, чем было.
 */

/** Имя параметра в адресе возврата. Второй конец — backend, тот же литерал. */
export const CHECKOUT_RETURN_PROOF_PARAM = 'rp';

/**
 * Имя параметра, которым мы называем серверу страницу предъявления. Второй
 * конец — backend (`CHECKOUT_RETURN_PROOF_PAGE_PARAM`), сверяет сторож двух
 * концов.
 */
export const CHECKOUT_RETURN_PROOF_PAGE_PARAM = 'page';

/**
 * Имя параметра, которым плеер называет себя. Второй конец — backend
 * (`CHECKOUT_RETURN_PROOF_PRESENTER_PARAM`), сверяет сторож двух концов.
 */
export const CHECKOUT_RETURN_PROOF_PRESENTER_PARAM = 'presenter';

/**
 * WEB-2493 — ПОТОЛОК ОЖИДАНИЯ ПРОВЕРКИ ПРИЗНАКА.
 *
 * Загрузка квиза ждёт ответа на этот вопрос (PublicQuiz.tsx, состояние
 * 'checking'). Потолка не было: зависшая сеть держала покупателя на
 * прелоадере, пока браузер сам не оборвёт запрос, — десятки секунд и больше.
 * По истечении потолка вопрос отменяется и читается как «признака нет» —
 * ровно как любой другой сбой (возврат идёт по следу в браузере).
 *
 * Почему 8 секунд — то же рассуждение, что у потолка резолва
 * (`RESOLVE_TIMEOUT_MS`, api/publicResolve.ts): вопрос идёт на абсолютный
 * адрес API, то есть на отдельное соединение, которое на медленной мобильной
 * сети ещё надо установить (TLS), а отвечает ручка одним булевым. Короче
 * брать нельзя: ложное «нет» роняет УЖЕ ЗАПЛАТИВШЕГО на первый экран квиза.
 *
 * Потолок накрывает и чтение тела ответа: сигнал отмены живёт до конца
 * `res.json()`.
 *
 * Сигнал — встроенный `AbortSignal.timeout` (самый короткий код: модуль в
 * первом экране, запас веса там — десятки байт). В старых браузерах без
 * него (Safari до 16) ограничения просто нет — запрос ждёт, как до
 * карточки: не хуже, чем было.
 */
export const CHECKOUT_RETURN_PROOF_TIMEOUT_MS = 8000;

/** Ключ хранилища вкладки, где живёт номер предъявителя. */
const PRESENTER_STORAGE_KEY = 'w2a_return_presenter';

/** Номер предъявителя этой загрузки страницы (память страницы). */
let presenterInMemory: string | null = null;

/**
 * Случайный номер. Берём криптографический источник, когда он есть: номер
 * не должен быть угадываемым — угадавший его посторонний с пересланным
 * адресом попал бы в чужое окно повтора. Источника нет (очень старый
 * браузер) — берём запасной, это всё равно лучше, чем общий на всех.
 */
function randomPresenterId(): string {
  const cryptoApi =
    typeof globalThis !== 'undefined'
      ? (globalThis.crypto as Crypto | undefined)
      : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    cryptoApi.getRandomValues(bytes);
    return Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }
  return `${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2)}${Math.random().toString(36).slice(2)}`;
}

/**
 * НОМЕР ПРЕДЪЯВИТЕЛЯ (фикс волны 6 свежего взгляда пачки 46, находка 1).
 *
 * Сервер держит короткое окно, в котором признак можно предъявить повторно
 * (потерянный ответ). Отличать «тот же покупатель переспросил» от «чужой
 * открыл пересланную ссылку» одной страницей нельзя: страница у них одна и
 * та же по построению. Поэтому плеер заводит случайный номер на загрузку
 * страницы и называет его серверу.
 *
 * ГДЕ ЖИВЁТ. В памяти страницы — всегда; в хранилище ВКЛАДКИ
 * (sessionStorage) — когда браузер его отдаёт: тогда перезагрузка вкладки
 * остаётся тем же предъявителем. Браузер хранилище не отдал (режим
 * «инкогнито» с запретом, выключённое хранение сайта, переполнение) — номер
 * живёт только в памяти: первый показ экрана оплаты работает, а после
 * перезагрузки возврат идёт по следу в браузере. Это принятая цена, а не
 * недосмотр.
 *
 * РЕШЕНИЯ НА БАННЕРЕ ЭТА ОТМЕТКА НЕ СПРАШИВАЕТ — и это сказано здесь прямо,
 * потому что раньше докблок обещал обратное (фикс волны 7). Отметка строго
 * необходима для возврата с оплаты, который покупатель сам запросил: она
 * случайная, признака посетителя не несёт, живёт до закрытия вкладки, а
 * наружу браузера уходит ТОЛЬКО нашему серверу — параметром `presenter=` в
 * вопросе о признаке возврата (`verifyCheckoutReturnProof` ниже), где хранится
 * в отметке о предъявлении не дольше самого признака. Прежний докблок обещал
 * «наружу не уходит» — неправда, поправлена волной 13 свежего взгляда пачки
 * 46 вслед за политикой (волна 11). В политике конфиденциальности запись
 * названа поимённо (`frontend/src/i18n/dictionaries/legal.ts`, раздел о
 * записях в браузере).
 *
 * Чтение и запись хранилища — в try/catch: обращение к нему само по себе
 * бросает там, где оно запрещено.
 */
export function currentPresenterId(): string {
  if (presenterInMemory) return presenterInMemory;
  try {
    const stored = window.sessionStorage.getItem(PRESENTER_STORAGE_KEY);
    if (stored) {
      presenterInMemory = stored;
      return stored;
    }
  } catch {
    // хранилище запрещено — дальше живём одной памятью страницы
  }
  const fresh = randomPresenterId();
  presenterInMemory = fresh;
  try {
    window.sessionStorage.setItem(PRESENTER_STORAGE_KEY, fresh);
  } catch {
    // то же самое: запрет хранения номера не отменяет
  }
  return fresh;
}

/**
 * ФИКС ВОЛНЫ 3 СВЕЖЕГО ВЗГЛЯДА ПАЧКИ 46 (находка 2) — СТРАНИЦА ПРЕДЪЯВЛЕНИЯ.
 *
 * Признак был предъявительским: подписан на проект и жил сутки, поэтому
 * пересланный адрес возврата открывал шаг оплаты кому угодно и у ЛЮБОГО
 * квиза проекта — ровно то, что закрывала защита WEB-1740. Теперь сервер
 * чеканит признак под конкретную страницу возврата и сверяет её при
 * проверке. Подменить страницу предъявитель не может: она берётся из
 * адресной строки той самой загрузки, которой признак что-то открывает, —
 * уйдя на другой квиз, он уносит и другую страницу.
 *
 * Форма одна с сервером: схема + хост + путь, без запроса и без якоря.
 */
export function currentReturnPage(): string {
  if (typeof window === 'undefined') return '';
  const { origin, pathname } = window.location;
  if (!origin || !pathname) return '';
  const page = `${origin}${pathname}`;
  return page.length > 1 && page.endsWith('/') ? page.slice(0, -1) : page;
}

/**
 * Достаёт признак из адреса. Пустое значение — это «нет признака», а не
 * пустая строка: иначе плеер пошёл бы спрашивать сервер ни о чём.
 */
export function readCheckoutReturnProof(
  search: string | URLSearchParams,
): string | null {
  const params =
    typeof search === 'string' ? new URLSearchParams(search) : search;
  const raw = params.get(CHECKOUT_RETURN_PROOF_PARAM);
  return raw && raw.length > 0 ? raw : null;
}

/**
 * Спрашивает сервер, наш ли это признак и того ли он проекта. Никогда не
 * бросает: ответ всегда булев. Дольше потолка не ждёт (WEB-2493: до карточки
 * докблок обещал «не зависает», а потолка у запроса не было).
 */
export async function verifyCheckoutReturnProof(
  projectId: string,
  token: string,
  /**
   * Страница предъявления (фикс волны 3, находка 2). Пустая — спрашивать
   * нечего: сервер без неё всё равно ответит «не годен».
   */
  page: string,
  /**
   * Номер предъявителя (фикс волны 6, находка 1). Пустой — спрашивать
   * нечего: сервер без него отвечает «не годен».
   */
  presenter: string,
): Promise<boolean> {
  if (!projectId || !token || !page || !presenter) return false;
  try {
    // WEB-2493: потолок ожидания — см. CHECKOUT_RETURN_PROOF_TIMEOUT_MS.
    const res = await fetch(
      `${API_BASE_URL}/public/paywall/return-proof/${encodeURIComponent(
        projectId,
      )}?token=${encodeURIComponent(token)}&${CHECKOUT_RETURN_PROOF_PAGE_PARAM}=${encodeURIComponent(
        page,
      )}&${CHECKOUT_RETURN_PROOF_PRESENTER_PARAM}=${encodeURIComponent(presenter)}`,
      // Без `?.`: сборка первого экрана разворачивает его в длинную форму.
      {
        signal:
          AbortSignal.timeout &&
          AbortSignal.timeout(CHECKOUT_RETURN_PROOF_TIMEOUT_MS),
      },
    );
    if (!res.ok) return false;
    // WEB-2493 (ради байтов первого экрана): короткая проверка вида ответа.
    // `null` бросает при чтении поля → catch → false; число, строка, массив
    // и объект без `valid: true` дают false — то же, что прежняя длинная
    // проверка `typeof … === 'object' && … !== null`.
    return ((await res.json()) as { valid?: unknown }).valid === true;
  } catch {
    return false;
  }
}
